import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LogOut, ChevronDown, HelpCircle, Settings } from "lucide-react";

import Logo from "@/components/ui/logo";
import { useAuth } from "@/context/AuthContext";
import "@/components/NavBar/NavBar.css";

function UserMenu() {
    const { user, logout } = useAuth();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const location = useLocation();

    useEffect(() => { setOpen(false); }, [location.pathname]);
    useEffect(() => {
        function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
        function onEsc(e) { if (e.key === "Escape") setOpen(false); }
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onEsc);
        return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onEsc); };
    }, []);

    if (!user) return null;
    const initial = (user.name || user.username || "?").trim().charAt(0).toUpperCase();

    return (
        <div className="wp-user" ref={ref}>
            <button className="wp-user__btn" onClick={() => setOpen((v) => !v)} aria-haspopup="menu" aria-expanded={open}>
                <span className="wp-user__avatar">
                    {user.avatar ? <img src={user.avatar} alt={user.name || user.username} /> : initial}
                </span>
                <span className="wp-user__name">{user.name || user.username}</span>
                <ChevronDown className="wp-user__chev" width={16} height={16} />
            </button>
            {open && (
                <div className="wp-user__menu" role="menu">
                    <div className="wp-user__head">
                        <span className="wp-user__avatar wp-user__avatar--lg">
                            {user.avatar ? <img src={user.avatar} alt="" /> : initial}
                        </span>
                        <div className="wp-user__meta">
                            <div className="wp-user__meta-name">{user.name || user.username}</div>
                            <div className="wp-user__meta-sub">{user.email || `@${user.username}`}</div>
                        </div>
                    </div>
                    <NavLink to="/help" className="wp-user__item" role="menuitem" onClick={() => setOpen(false)}>
                        <HelpCircle width={16} height={16} /> Help
                    </NavLink>
                    <NavLink to="/settings" className="wp-user__item" role="menuitem" onClick={() => setOpen(false)}>
                        <Settings width={16} height={16} /> Settings
                    </NavLink>
                    <button className="wp-user__item wp-user__item--danger" onClick={logout} role="menuitem">
                        <LogOut width={16} height={16} /> Sign out
                    </button>
                </div>
            )}
        </div>
    );
}

export default function NavBar() {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const savedBodyOverflow = useRef(null);

    // Close mobile menu on navigation
    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    // Close on Esc
    useEffect(() => {
        function onKey(e) {
            if (e.key === "Escape") setOpen(false);
        }
        if (open) window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    // Disable body scroll while panel is open
    useEffect(() => {
        const body = document && document.body;
        if (!body) return;

        if (open) {
            // store previous overflow so we can restore it
            savedBodyOverflow.current = body.style.overflow;
            body.style.overflow = "hidden";
        } else {
            // restore previous overflow
            if (savedBodyOverflow.current !== null) {
                body.style.overflow = savedBodyOverflow.current;
                savedBodyOverflow.current = null;
            } else {
                body.style.overflow = "";
            }
        }

        // cleanup on unmount: restore overflow
        return () => {
            if (body && savedBodyOverflow.current !== null) {
                body.style.overflow = savedBodyOverflow.current;
                savedBodyOverflow.current = null;
            }
        };
    }, [open]);

    return (
        <nav className="wp-navbar-container">
            <div className="wp-navbar max-w-6xl mx-auto px-4 lg:px-0" role="navigation" aria-label="Main">
                <div className="wp-navbar__brand">
                    <NavLink to="/" className="wp-navbar__logo"><Logo /></NavLink>
                </div>

                <div
                    id="wp-navbar-links"
                    className={`wp-navbar__links ${open ? "open" : ""}`}
                >
                    {/* Close button inside sliding panel (mobile) */}
                    <button
                        className="wp-navbar__panel-close block md:hidden"
                        aria-label="Close menu"
                        onClick={() => setOpen(false)}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
                            <path fill="currentColor" d="M18.3 5.71L12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.29 19.71 2.88 18.3 9.19 12 2.88 5.71 4.29 4.29 10.59 10.6 16.88 4.29z" />
                        </svg>
                    </button>

                    <Logo className="h-24 block md:hidden" />

                    <NavLink to="/" className={({ isActive }) => isActive ? "md:hidden active" : "md:hidden"}>Home</NavLink>
                    {/* add more links here as needed */}
                </div>

                <UserMenu />
            </div>
        </nav>
    );
}